import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { StatusBadge, PassTypeBadge, formatDate, formatDateTime } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { lookupStudent, searchStudents, studentRequestPass, getStudentRequests, getCourses } from "../lib/api";
import { Search, FileText, Plus, Clock, CheckCircle2, XCircle, Eye, Printer, Loader2, User, ArrowLeft, Ban } from "lucide-react";
import WebcamCapture from "../components/WebcamCapture";

const PIMS_LOGO = "https://customer-assets.emergentagent.com/job_gpass-approval-hub/artifacts/aqa37c60_pims%20logo.png";

const StudentPortal = () => {
  const [step, setStep] = useState("search"); // search, profile, form, requests
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [student, setStudent] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [courses, setCourses] = useState([]);
  const [studentPhoto, setStudentPhoto] = useState(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    pass_type: "",
    reason: "",
    exit_date: "",
    exit_time: "",
    expected_return_date: "",
    expected_return_time: "",
    destination: "",
    emergency_contact: "",
    student_remarks: "",
  });

  useEffect(() => {
    getCourses().then(({ data }) => setCourses(data)).catch(() => {});
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Enter your registration number or name");
      return;
    }
    
    setSearching(true);
    try {
      const { data } = await searchStudents(searchQuery);
      setSearchResults(data);
      if (data.length === 0) {
        toast.info("No students found. Please check your registration number.");
      }
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleSelectStudent = async (selectedStudent) => {
    setStudent(selectedStudent);
    setFormData({ ...formData, emergency_contact: selectedStudent.mobile_number });
    setStep("profile");
    loadStudentRequests(selectedStudent.registration_number);
  };

  const loadStudentRequests = async (regNumber) => {
    setLoadingRequests(true);
    try {
      const { data } = await getStudentRequests(regNumber);
      setRequests(data);
    } catch (error) {
      console.error("Failed to load requests");
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    
    if (!formData.pass_type || !formData.reason || !formData.exit_date || 
        !formData.exit_time || !formData.expected_return_date || 
        !formData.expected_return_time || !formData.destination) {
      toast.error("Please fill all required fields");
      return;
    }

    if (!studentPhoto) {
      toast.error("Student photo is mandatory. Please capture a photo using the webcam.");
      return;
    }

    setSubmitting(true);
    try {
      await studentRequestPass({
        registration_number: student.registration_number,
        ...formData,
        student_photo: studentPhoto
      });
      toast.success("Gate pass request submitted successfully!");
      setStep("profile");
      setStudentPhoto(null);
      setFormData({
        pass_type: "",
        reason: "",
        exit_date: "",
        exit_time: "",
        expected_return_date: "",
        expected_return_time: "",
        destination: "",
        emergency_contact: student.mobile_number,
        student_remarks: "",
      });
      loadStudentRequests(student.registration_number);
    } catch (error) {
      toast.error(error.response?.data?.detail ? (typeof error.response.data.detail === "string" ? error.response.data.detail : "Validation error. Please check all fields.") : "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status.startsWith("pending")).length,
    approved: requests.filter(r => r.status === "approved" || r.status === "gate_verified").length,
    rejected: requests.filter(r => r.status.startsWith("rejected")).length,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img src={PIMS_LOGO} alt="PIMS" className="h-12" />
            <div>
              <h1 className="text-lg font-semibold text-slate-900">Gate Pass Portal</h1>
              <p className="text-xs text-slate-500">Student Self-Service</p>
            </div>
          </div>
          {student && (
            <Button variant="ghost" onClick={() => { setStudent(null); setStep("search"); setSearchResults([]); }}>
              Change Student
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Search Step */}
        {step === "search" && (
          <div className="max-w-xl mx-auto space-y-6" data-testid="student-search">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-semibold text-slate-900">Find Your Profile</h2>
              <p className="text-slate-500 mt-2">Enter your Registration Number to access gate pass services</p>
            </div>

            <Card className="border-slate-200">
              <CardContent className="pt-6">
                <div className="flex gap-3">
                  <Input
                    placeholder="Enter Registration Number or Name"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="flex-1"
                    data-testid="registration-search-input"
                  />
                  <Button onClick={handleSearch} disabled={searching} data-testid="search-btn">
                    {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    <span className="ml-2">Search</span>
                  </Button>
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="mt-6 space-y-3">
                    <p className="text-sm text-slate-500">Select your profile:</p>
                    {searchResults.map((s) => (
                      <div 
                        key={s.id}
                        className="p-4 border border-slate-200 rounded-lg hover:border-slate-400 cursor-pointer transition-colors"
                        onClick={() => handleSelectStudent(s)}
                        data-testid={`student-result-${s.id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-slate-900">{s.name}</p>
                            <p className="text-sm text-slate-500">S/o {s.father_name}</p>
                            <p className="text-sm text-slate-500">{s.course_name} - Year {s.year}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-sm text-blue-600">{s.registration_number}</p>
                            <p className="text-xs text-slate-400">{s.mobile_number}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <p className="text-center text-sm text-slate-500">
              Don't have a registration number? Contact the admin office.
            </p>
          </div>
        )}

        {/* Profile Step */}
        {step === "profile" && student && (
          <div className="space-y-6" data-testid="student-profile">
            {/* Student Info Card */}
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      {student.name}
                    </CardTitle>
                    <CardDescription>S/o {student.father_name}</CardDescription>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-lg font-semibold text-blue-600">{student.registration_number}</p>
                    <p className="text-sm text-slate-500">{student.course_name} - Year {student.year}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Mobile Number</p>
                    <p className="font-medium">{student.mobile_number}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Parent Phone</p>
                    <p className="font-medium">{student.parent_phone || "-"}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Hostel Status</p>
                    <p className="font-medium capitalize">{student.hostel_status?.replace("_", " ")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Total</p>
                      <p className="text-2xl font-semibold">{stats.total}</p>
                    </div>
                    <FileText className="h-8 w-8 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Pending</p>
                      <p className="text-2xl font-semibold text-amber-600">{stats.pending}</p>
                    </div>
                    <Clock className="h-8 w-8 text-amber-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Approved</p>
                      <p className="text-2xl font-semibold text-emerald-600">{stats.approved}</p>
                    </div>
                    <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Rejected</p>
                      <p className="text-2xl font-semibold text-red-600">{stats.rejected}</p>
                    </div>
                    <XCircle className="h-8 w-8 text-red-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Action Button */}
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">My Gate Pass Requests</h3>
              {student.pass_allowed !== false ? (
                <Button onClick={() => { setStep("form"); setStudentPhoto(null); }} data-testid="new-request-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Gate Pass Request
                </Button>
              ) : (
                <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg" data-testid="pass-not-allowed-banner">
                  <Ban className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium text-red-700">Pass Not Allowed - Contact Administrator</span>
                </div>
              )}
            </div>

            {/* Requests Table */}
            <Card className="border-slate-200">
              <CardContent className="p-0">
                {loadingRequests ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                  </div>
                ) : requests.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>No gate pass requests yet</p>
                    <Button variant="link" onClick={() => setStep("form")} className="mt-2">
                      Create your first request
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Request No.</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Exit Date</TableHead>
                        <TableHead>Destination</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {requests.map((req) => (
                        <TableRow key={req.id} data-testid={`request-row-${req.id}`}>
                          <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                          <TableCell><PassTypeBadge type={req.pass_type} /></TableCell>
                          <TableCell>{formatDate(req.exit_date)}</TableCell>
                          <TableCell className="max-w-[150px] truncate">{req.destination}</TableCell>
                          <TableCell><StatusBadge status={req.status} /></TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setSelectedPass(req)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {(req.status === "approved" || req.status === "gate_verified") && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => navigate(`/print/${req.id}`)}
                                >
                                  <Printer className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Form Step */}
        {step === "form" && student && (
          <div className="max-w-2xl mx-auto" data-testid="gate-pass-form">
            <Button variant="ghost" onClick={() => setStep("profile")} className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Profile
            </Button>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>New Gate Pass Request</CardTitle>
                <CardDescription>Fill in the details for your gate pass request</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitRequest} className="space-y-6">
                  {/* Student Info (Read-only) */}
                  <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg text-sm">
                    <div>
                      <p className="text-slate-500">Name</p>
                      <p className="font-medium">{student.name}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Registration No.</p>
                      <p className="font-medium font-mono">{student.registration_number}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Course / Year</p>
                      <p className="font-medium">{student.course_name} - Year {student.year}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Father's Name</p>
                      <p className="font-medium">{student.father_name}</p>
                    </div>
                  </div>

                  {/* Pass Type */}
                  <div className="space-y-2">
                    <Label>Pass Type *</Label>
                    <Select 
                      value={formData.pass_type} 
                      onValueChange={(v) => setFormData({...formData, pass_type: v})}
                    >
                      <SelectTrigger data-testid="pass-type-select">
                        <SelectValue placeholder="Select pass type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short_leave">Short Leave</SelectItem>
                        <SelectItem value="day_pass">Day Pass</SelectItem>
                        <SelectItem value="emergency_leave">Emergency Leave</SelectItem>
                        <SelectItem value="home_visit">Home Visit</SelectItem>
                        <SelectItem value="medical_leave">Medical Leave</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Reason */}
                  <div className="space-y-2">
                    <Label>Reason for Leave *</Label>
                    <Textarea
                      placeholder="Enter the reason for your gate pass request"
                      value={formData.reason}
                      onChange={(e) => setFormData({...formData, reason: e.target.value})}
                      data-testid="reason-input"
                      rows={3}
                    />
                  </div>

                  {/* Exit Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Exit Date *</Label>
                      <Input
                        type="date"
                        value={formData.exit_date}
                        onChange={(e) => setFormData({...formData, exit_date: e.target.value})}
                        data-testid="exit-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Exit Time *</Label>
                      <Input
                        type="time"
                        value={formData.exit_time}
                        onChange={(e) => setFormData({...formData, exit_time: e.target.value})}
                        data-testid="exit-time-input"
                      />
                    </div>
                  </div>

                  {/* Return Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Expected Return Date *</Label>
                      <Input
                        type="date"
                        value={formData.expected_return_date}
                        onChange={(e) => setFormData({...formData, expected_return_date: e.target.value})}
                        data-testid="return-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Expected Return Time *</Label>
                      <Input
                        type="time"
                        value={formData.expected_return_time}
                        onChange={(e) => setFormData({...formData, expected_return_time: e.target.value})}
                        data-testid="return-time-input"
                      />
                    </div>
                  </div>

                  {/* Destination */}
                  <div className="space-y-2">
                    <Label>Destination / Address *</Label>
                    <Input
                      placeholder="Enter your destination address"
                      value={formData.destination}
                      onChange={(e) => setFormData({...formData, destination: e.target.value})}
                      data-testid="destination-input"
                    />
                  </div>

                  {/* Emergency Contact */}
                  <div className="space-y-2">
                    <Label>Emergency Contact Number</Label>
                    <Input
                      placeholder="Enter emergency contact number"
                      value={formData.emergency_contact}
                      onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
                      data-testid="emergency-contact-input"
                    />
                  </div>

                  {/* Remarks */}
                  <div className="space-y-2">
                    <Label>Additional Remarks (Optional)</Label>
                    <Textarea
                      placeholder="Any additional information"
                      value={formData.student_remarks}
                      onChange={(e) => setFormData({...formData, student_remarks: e.target.value})}
                      rows={2}
                    />
                  </div>

                  {/* Webcam Capture */}
                  <WebcamCapture
                    onCapture={setStudentPhoto}
                    onClear={() => setStudentPhoto(null)}
                    capturedPhoto={studentPhoto}
                  />

                  {/* Submit */}
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setStep("profile")}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting} data-testid="submit-request-btn">
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        "Submit Request"
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* View Pass Dialog */}
        <Dialog open={!!selectedPass} onOpenChange={() => setSelectedPass(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Gate Pass Details</DialogTitle>
            </DialogHeader>
            
            {selectedPass && (
              <div className="space-y-6">
                {/* Header Info */}
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-mono text-sm text-slate-500">{selectedPass.request_number}</p>
                    <h3 className="text-lg font-semibold">{selectedPass.student_name}</h3>
                  </div>
                  <StatusBadge status={selectedPass.status} />
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Pass Type</p>
                    <p className="font-medium capitalize">{selectedPass.pass_type?.replace("_", " ")}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Course / Year</p>
                    <p className="font-medium">{selectedPass.course_name} - Year {selectedPass.year}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Exit Date & Time</p>
                    <p className="font-medium">{formatDate(selectedPass.exit_date)} at {selectedPass.exit_time}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Return Date & Time</p>
                    <p className="font-medium">{formatDate(selectedPass.expected_return_date)} at {selectedPass.expected_return_time}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-500">Destination</p>
                    <p className="font-medium">{selectedPass.destination}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-500">Reason</p>
                    <p className="font-medium">{selectedPass.reason}</p>
                  </div>
                </div>

                {/* Approval History */}
                {selectedPass.approval_history && selectedPass.approval_history.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3">Approval History</h4>
                    <div className="space-y-3">
                      {selectedPass.approval_history.map((item, idx) => (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg border ${
                            item.action === "approve" 
                              ? "bg-emerald-50 border-emerald-200" 
                              : "bg-red-50 border-red-200"
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{item.approver_name}</p>
                              <p className="text-xs text-slate-500 capitalize">
                                {item.stage?.replace(/_/g, " ")}
                              </p>
                            </div>
                            <span className={`text-xs font-medium px-2 py-1 rounded ${
                              item.action === "approve" 
                                ? "bg-emerald-100 text-emerald-700" 
                                : "bg-red-100 text-red-700"
                            }`}>
                              {item.action === "approve" ? "Approved" : "Rejected"}
                            </span>
                          </div>
                          {item.rejection_reason && (
                            <p className="text-sm mt-2 text-red-600">Reason: {item.rejection_reason}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-2">{formatDateTime(item.timestamp)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default StudentPortal;
